import { useEffect, useRef, useState } from "react";

import { AppStrings } from "../../../../../../utils/constants/AppStrings";
import {
  formatTimeLeftInWindow,
  LOGIN_ROW_VARIANT,
  LOGIN_STATUS,
  LOGIN_TYPE_ENROLLMENT,
  LOGIN_TYPE_GAME_PASS,
  resolveLoginRewardHits,
} from "../loginStreakHistory";

const EMPTY_CELL = "-";

const muted = (value) =>
  value && value !== EMPTY_CELL ? (
    value
  ) : (
    <span className="user-manager-game-pass-monthly-login-muted">
      {EMPTY_CELL}
    </span>
  );

const typeToneClass = (type) => {
  if (type === LOGIN_TYPE_GAME_PASS) {
    return "user-manager-game-pass-monthly-login-type--gamepass";
  }
  if (type === LOGIN_TYPE_ENROLLMENT) {
    return "user-manager-game-pass-monthly-login-type--enroll";
  }
  return "user-manager-game-pass-monthly-login-type--signin";
};

const TypeChip = ({ type, label }) => (
  <span className={`user-manager-game-pass-monthly-login-type ${typeToneClass(type)}`}>
    <span className="user-manager-game-pass-monthly-login-type-dot" />
    {label}
  </span>
);

const ConsecutiveChip = ({ value, reset }) => {
  if (!value || value === EMPTY_CELL) {
    return muted(value);
  }
  return (
    <span
      className={`user-manager-game-pass-monthly-login-daynum${
        reset ? " user-manager-game-pass-monthly-login-daynum--reset" : ""
      }`}
    >
      {value}
    </span>
  );
};

const StatusTag = ({ status, label }) => {
  let tone = "ok";
  if (status === LOGIN_STATUS.FIRST) tone = "first";
  if (status === LOGIN_STATUS.RESTARTED) tone = "restart";
  if (status === LOGIN_STATUS.PENDING) tone = "pending";
  return (
    <span
      className={`user-manager-game-pass-monthly-login-tag user-manager-game-pass-monthly-login-tag--${tone}`}
    >
      {label}
    </span>
  );
};

const TimeLeftCell = ({
  amount,
  sub,
  closeCall = false,
  liveWindowEnd,
  onExpire,
}) => {
  const [now, setNow] = useState(() => Date.now());
  const expiredNotified = useRef(false);

  useEffect(() => {
    if (!liveWindowEnd) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [liveWindowEnd]);

  const live = liveWindowEnd
    ? formatTimeLeftInWindow({
        windowEnd: liveWindowEnd,
        now,
        pending: true,
      })
    : null;

  useEffect(() => {
    if (live?.expired && !expiredNotified.current) {
      expiredNotified.current = true;
      onExpire?.();
    }
  }, [live?.expired, onExpire]);

  const displayAmount = live?.amount ?? amount;
  const displaySub = live?.sub ?? sub;
  const isCloseCall = live?.closeCall ?? closeCall;
  const isLive = Boolean(liveWindowEnd) || live?.live;

  if (!displayAmount || displayAmount === EMPTY_CELL) {
    return muted(displayAmount);
  }
  return (
    <span
      className={`user-manager-game-pass-monthly-login-window${
        isLive ? " user-manager-game-pass-monthly-login-window--live" : ""
      }${
        isCloseCall
          ? " user-manager-game-pass-monthly-login-window--close-call"
          : ""
      }`}
    >
      <span
        className={`user-manager-game-pass-monthly-login-window-amount${
          isLive || isCloseCall
            ? " user-manager-game-pass-monthly-login-window-amount--amber"
            : ""
        }`}
      >
        {displayAmount}
      </span>
      {displaySub ? (
        <span className="user-manager-game-pass-monthly-login-window-sub">
          {displaySub}
        </span>
      ) : null}
    </span>
  );
};

const BreakBanner = ({ label }) => (
  <span className="user-manager-game-pass-monthly-login-break">
    <span className="user-manager-game-pass-monthly-login-break-icon" aria-hidden="true">
      !
    </span>
    {label}
  </span>
);

export const LoginStreakHistoryLegend = () => (
  <div className="user-manager-game-pass-monthly-login-legend">
    <span>
      <span className="user-manager-game-pass-monthly-login-swatch user-manager-game-pass-monthly-login-swatch--green" />
      {AppStrings.GAME_PASS_MONTHLY_LOGIN_LEGEND_STREAK_DAY}
    </span>
    <span>
      <span className="user-manager-game-pass-monthly-login-swatch user-manager-game-pass-monthly-login-swatch--amber" />
      {AppStrings.GAME_PASS_MONTHLY_LOGIN_LEGEND_WINDOW}
    </span>
    <span>
      <span className="user-manager-game-pass-monthly-login-swatch user-manager-game-pass-monthly-login-swatch--red" />
      {AppStrings.GAME_PASS_MONTHLY_LOGIN_LEGEND_BROKEN}
    </span>
  </div>
);

/**
 * Turns serializable login-history rows into table cells with the Daily Login
 * chrome (type chips, consecutive-day badges, status tags, break banners).
 * Reward labels are filled from the live tier track when a day hits a node.
 */
export const decorateLoginStreakHistoryRows = (
  rows = [],
  { milestones = [], onWindowExpired } = {},
) =>
  resolveLoginRewardHits(rows, milestones).map((row) => {
    if (row.variant === LOGIN_ROW_VARIANT.BREAK) {
      return {
        ...row,
        cells: [
          <BreakBanner
            key={`${row.id}-break`}
            label={row.presentation?.breakLabel || row.cells?.[0]}
          />,
        ],
      };
    }

    const presentation = row.presentation || {};
    const rewardHit =
      presentation.rewardHit && presentation.rewardHit !== EMPTY_CELL
        ? presentation.rewardHit
        : EMPTY_CELL;
    const isPending = row.variant === LOGIN_ROW_VARIANT.PENDING;

    return {
      ...row,
      rowClassName: `user-manager-game-pass-monthly-table-row--${row.variant}`,
      cells: [
        isPending ? (
          <span
            key={`${row.id}-login`}
            className="user-manager-game-pass-monthly-login-muted"
          >
            {row.cells?.[0] || AppStrings.GAME_PASS_MONTHLY_LOGIN_NOT_LOGGED_IN_YET}
          </span>
        ) : (
          row.cells?.[0]
        ),
        presentation.type ? (
          <TypeChip
            key={`${row.id}-type`}
            type={presentation.type}
            label={row.cells?.[1]}
          />
        ) : (
          muted(row.cells?.[1])
        ),
        row.cells?.[2],
        <ConsecutiveChip
          key={`${row.id}-streak`}
          value={presentation.consecutiveDays}
          reset={presentation.consecutiveReset}
        />,
        <TimeLeftCell
          key={`${row.id}-window`}
          amount={presentation.timeLeftAmount}
          sub={presentation.timeLeftSub}
          closeCall={presentation.timeLeftCloseCall}
          liveWindowEnd={
            presentation.timeLeftLive ? presentation.windowEnd : undefined
          }
          onExpire={onWindowExpired}
        />,
        presentation.status ? (
          <StatusTag
            key={`${row.id}-status`}
            status={presentation.status}
            label={row.cells?.[5]}
          />
        ) : (
          muted(row.cells?.[5])
        ),
        presentation.streakBroken && presentation.streakBroken !== EMPTY_CELL
          ? presentation.streakBroken
          : muted(presentation.streakBroken),
        rewardHit !== EMPTY_CELL ? rewardHit : muted(rewardHit),
      ],
    };
  });
